import React, { useMemo, useState } from 'react';
import { XIcon, SearchIcon, CheckCircle2Icon, AlertCircleIcon, UserIcon, UsersIcon, DollarSignIcon, CalendarIcon, ShieldCheckIcon, InfoIcon } from 'lucide-react';
import { DraggableModal } from './ui/DraggableModal';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  approvalLimit: number;
  isAvailable: boolean;
  unavailableReason?: string;
  unavailableUntil?: string;
}
interface Team {
  id: string;
  name: string;
  department: string;
  memberCount: number;
  averageApprovalLimit: number;
}
interface ReassignModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentAmount: number;
  currentAssignee: string;
  onReassign: (assigneeId: string, assigneeType: 'user' | 'team') => void;
}
// Mock users data with delegation rules
const mockUsers: User[] = [{
  id: 'user-1',
  name: 'Sarah Chen',
  email: 'sarah.chen@company.com',
  role: 'AP Manager',
  department: 'Finance',
  approvalLimit: 100000,
  isAvailable: true
}, {
  id: 'user-2',
  name: 'Mike Johnson',
  email: 'mike.johnson@company.com',
  role: 'AP Supervisor',
  department: 'Finance',
  approvalLimit: 50000,
  isAvailable: false,
  unavailableReason: 'On leave',
  unavailableUntil: '15/02/2024'
}, {
  id: 'user-3',
  name: 'Emma Wilson',
  email: 'emma.wilson@company.com',
  role: 'AP Specialist',
  department: 'Finance',
  approvalLimit: 10000,
  isAvailable: true
}, {
  id: 'user-4',
  name: 'James Brown',
  email: 'james.brown@company.com',
  role: 'Finance Director',
  department: 'Finance',
  approvalLimit: 500000,
  isAvailable: true
}, {
  id: 'user-5',
  name: 'Lisa Martinez',
  email: 'lisa.martinez@company.com',
  role: 'AP Clerk',
  department: 'Finance',
  approvalLimit: 5000,
  isAvailable: false,
  unavailableReason: 'Out of office',
  unavailableUntil: '10/02/2024'
}, {
  id: 'user-6',
  name: 'David Lee',
  email: 'david.lee@company.com',
  role: 'AP Specialist',
  department: 'Finance',
  approvalLimit: 15000,
  isAvailable: true
}];
const mockTeams: Team[] = [{
  id: 'team-1',
  name: 'AP Team',
  department: 'Finance',
  memberCount: 8,
  averageApprovalLimit: 25000
}, {
  id: 'team-2',
  name: 'Finance Team',
  department: 'Finance',
  memberCount: 12,
  averageApprovalLimit: 75000
}, {
  id: 'team-3',
  name: 'Procurement Team',
  department: 'Operations',
  memberCount: 6,
  averageApprovalLimit: 50000
}];
export function ReassignModal({
  isOpen,
  onClose,
  documentAmount,
  currentAssignee,
  onReassign
}: ReassignModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState<'users' | 'teams'>('users');
  const [selectedAssignee, setSelectedAssignee] = useState<{
    id: string;
    type: 'user' | 'team';
  } | null>(null);
  // Filter and sort users based on eligibility
  const filteredUsers = useMemo(() => {
    const filtered = mockUsers.filter(user => user.name.toLowerCase().includes(searchQuery.toLowerCase()) || user.email.toLowerCase().includes(searchQuery.toLowerCase()) || user.role.toLowerCase().includes(searchQuery.toLowerCase()));
    // Sort by eligibility: available + sufficient limit first
    return filtered.sort((a, b) => {
      const aEligible = a.isAvailable && a.approvalLimit >= documentAmount;
      const bEligible = b.isAvailable && b.approvalLimit >= documentAmount;
      if (aEligible && !bEligible) return -1;
      if (!aEligible && bEligible) return 1;
      // If both eligible or both not, sort by approval limit descending
      return b.approvalLimit - a.approvalLimit;
    });
  }, [searchQuery, documentAmount]);
  const filteredTeams = useMemo(() => {
    return mockTeams.filter(team => team.name.toLowerCase().includes(searchQuery.toLowerCase()) || team.department.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery]);
  const canReassign = (user: User) => {
    return user.isAvailable && user.approvalLimit >= documentAmount;
  };
  const getReassignmentIssues = (user: User): string[] => {
    const issues: string[] = [];
    if (!user.isAvailable) {
      issues.push(`${user.unavailableReason} until ${user.unavailableUntil || 'unknown date'}`);
    }
    if (user.approvalLimit < documentAmount) {
      issues.push(`Approval limit ($${user.approvalLimit.toLocaleString()}) is below document amount ($${documentAmount.toLocaleString()})`);
    }
    return issues;
  };
  const handleReassign = () => {
    if (selectedAssignee) {
      onReassign(selectedAssignee.id, selectedAssignee.type);
      onClose();
    }
  };
  return <DraggableModal isOpen={isOpen} onClose={onClose} title="Reassign Document" defaultWidth={700} defaultHeight={600} minWidth={600} minHeight={400} data-id="element-1915">
      <div className="flex flex-col h-full" data-id="element-1916">
        {/* Header Info */}
        <div className="px-6 py-4 border-b border-gray-200 bg-blue-50 flex-shrink-0" data-id="element-1917">
          <div className="flex items-start justify-between" data-id="element-1918">
            <div className="flex-1" data-id="element-1919">
              <div className="flex items-center gap-2 mb-2" data-id="element-1920">
                <InfoIcon className="w-4 h-4 text-blue-600" data-id="element-1921" />
                <span className="text-sm font-medium text-blue-900" data-id="element-1922">
                  Document Amount: ${documentAmount.toLocaleString()}
                </span>
              </div>
              <p className="text-xs text-blue-700" data-id="element-1923">
                Only users with sufficient approval limits and availability can
                be assigned this document
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-blue-100 rounded-lg transition-colors" data-id="element-1924">
              <XIcon className="w-5 h-5 text-blue-600" data-id="element-1925" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0" data-id="element-1926">
          <div className="relative" data-id="element-1927">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1928" />
            <Input type="text" placeholder="Search by name, email, or role..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 w-full" data-id="element-1929" />
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-gray-200 flex-shrink-0" data-id="element-1930">
          <div className="flex gap-4" data-id="element-1931">
            <button onClick={() => setSelectedTab('users')} className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${selectedTab === 'users' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1932">
              <div className="flex items-center gap-2" data-id="element-1933">
                <UserIcon className="w-4 h-4" data-id="element-1934" />
                Users ({filteredUsers.length})
              </div>
            </button>
            <button onClick={() => setSelectedTab('teams')} className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${selectedTab === 'teams' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1935">
              <div className="flex items-center gap-2" data-id="element-1936">
                <UsersIcon className="w-4 h-4" data-id="element-1937" />
                Teams ({filteredTeams.length})
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-1938">
          {selectedTab === 'users' ? <div className="space-y-3" data-id="element-1939">
              {filteredUsers.map(user => {
            const eligible = canReassign(user);
            const issues = getReassignmentIssues(user);
            const isSelected = selectedAssignee?.id === user.id && selectedAssignee?.type === 'user';
            return <button key={user.id} onClick={() => eligible ? setSelectedAssignee({
              id: user.id,
              type: 'user'
            }) : null} disabled={!eligible} className={`w-full text-left p-4 rounded-lg border-2 transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : eligible ? 'border-gray-200 hover:border-blue-300 hover:bg-gray-50' : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'}`} data-id="element-1940">
                    <div className="flex items-start justify-between" data-id="element-1941">
                      <div className="flex-1" data-id="element-1942">
                        <div className="flex items-center gap-3 mb-2" data-id="element-1943">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold" data-id="element-1944">
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div data-id="element-1945">
                            <div className="font-medium text-gray-900" data-id="element-1946">
                              {user.name}
                            </div>
                            <div className="text-xs text-gray-600" data-id="element-1947">
                              {user.role} • {user.department}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-xs" data-id="element-1948">
                          <div className="flex items-center gap-1" data-id="element-1949">
                            <DollarSignIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-1950" />
                            <span className={user.approvalLimit >= documentAmount ? 'text-green-700 font-medium' : 'text-red-700 font-medium'} data-id="element-1951">
                              ${user.approvalLimit.toLocaleString()} limit
                            </span>
                          </div>
                          <div className="flex items-center gap-1" data-id="element-1952">
                            <CalendarIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-1953" />
                            <span className={user.isAvailable ? 'text-green-700 font-medium' : 'text-red-700 font-medium'} data-id="element-1954">
                              {user.isAvailable ? 'Available' : 'Unavailable'}
                            </span>
                          </div>
                        </div>

                        {issues.length > 0 && <div className="mt-3 space-y-1" data-id="element-1955">
                            {issues.map((issue, index) => <div key={index} className="flex items-start gap-2 text-xs text-red-700 bg-red-50 px-2 py-1.5 rounded" data-id="element-1956">
                                <AlertCircleIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" data-id="element-1957" />
                                <span data-id="element-1958">{issue}</span>
                              </div>)}
                          </div>}
                      </div>

                      {eligible && <div className="ml-4" data-id="element-1959">
                          {isSelected ? <CheckCircle2Icon className="w-5 h-5 text-blue-600" data-id="element-1960" /> : <div className="w-5 h-5 rounded-full border-2 border-gray-300" data-id="element-1961" />}
                        </div>}
                    </div>
                  </button>;
          })}
            </div> : <div className="space-y-3" data-id="element-1962">
              {filteredTeams.map(team => {
            const isSelected = selectedAssignee?.id === team.id && selectedAssignee?.type === 'team';
            return <button key={team.id} onClick={() => setSelectedAssignee({
              id: team.id,
              type: 'team'
            })} className={`w-full text-left p-4 rounded-lg border-2 transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`} data-id="element-1963">
                    <div className="flex items-start justify-between" data-id="element-1964">
                      <div className="flex-1" data-id="element-1965">
                        <div className="flex items-center gap-3 mb-2" data-id="element-1966">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center" data-id="element-1967">
                            <UsersIcon className="w-5 h-5 text-white" data-id="element-1968" />
                          </div>
                          <div data-id="element-1969">
                            <div className="font-medium text-gray-900" data-id="element-1970">
                              {team.name}
                            </div>
                            <div className="text-xs text-gray-600" data-id="element-1971">
                              {team.department} • {team.memberCount} members
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-xs" data-id="element-1972">
                          <div className="flex items-center gap-1" data-id="element-1973">
                            <DollarSignIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-1974" />
                            <span className="text-gray-700" data-id="element-1975">
                              Avg. ${team.averageApprovalLimit.toLocaleString()}{' '}
                              limit
                            </span>
                          </div>
                        </div>
                      </div>

                      {isSelected ? <CheckCircle2Icon className="w-5 h-5 text-blue-600" data-id="element-1976" /> : <div className="w-5 h-5 rounded-full border-2 border-gray-300" data-id="element-1977" />}
                    </div>
                  </button>;
          })}
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0" data-id="element-1978">
          <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1979">
            <ShieldCheckIcon className="w-4 h-4" data-id="element-1980" />
            <span data-id="element-1981">
              {selectedAssignee ? 'Ready to reassign' : 'Select a user or team to continue'}
            </span>
          </div>
          <div className="flex gap-3" data-id="element-1982">
            <Button variant="primary" onClick={handleReassign} disabled={!selectedAssignee} data-id="element-1983">
              Reassign Document
            </Button>
            <Button variant="ghost" onClick={onClose} data-id="element-1984">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </DraggableModal>;
}